import React, { useState } from 'react';
import { ShieldAlert, TrendingUp, Loader2, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Zap } from 'lucide-react';
import api from '../api';

const SEVERITY_STYLES = {
  High: 'text-red-400 bg-red-900/20 border-red-700/40',
  Medium: 'text-amber-400 bg-amber-900/20 border-amber-700/40',
  Low: 'text-blue-400 bg-blue-900/20 border-blue-700/40',
};

const HEALTH_STYLES = {
  Critical: { bar: 'bg-red-500', text: 'text-red-400', badge: 'bg-red-900/30 text-red-400 border-red-700/40' },
  'At Risk': { bar: 'bg-amber-500', text: 'text-amber-400', badge: 'bg-amber-900/30 text-amber-400 border-amber-700/40' },
  'Needs Attention': { bar: 'bg-yellow-500', text: 'text-yellow-400', badge: 'bg-yellow-900/30 text-yellow-400 border-yellow-700/40' },
  Healthy: { bar: 'bg-green-500', text: 'text-green-400', badge: 'bg-green-900/30 text-green-400 border-green-700/40' },
  Excellent: { bar: 'bg-primary', text: 'text-primary', badge: 'bg-primary/20 text-primary border-primary/30' },
};

export function AIRiskScanner({ roadmapId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const runScan = async () => {
    if (result) { setExpanded(!expanded); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await api.request('/api/ai/roadmap-risks', {
        method: 'POST',
        body: JSON.stringify({ roadmap_id: roadmapId }),
      });
      setResult(data);
      setExpanded(true);
    } catch (e) {
      setError('Risk scan failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const healthStyle = result ? (HEALTH_STYLES[result.health_label] || HEALTH_STYLES['Needs Attention']) : null;
  const highRisks = result?.risks?.filter(r => r.severity === 'High') || [];
  const otherRisks = result?.risks?.filter(r => r.severity !== 'High') || [];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="ai-risk-scanner">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-400" />
          <span className="font-semibold">AI Risk Scanner</span>
          {result && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${healthStyle.badge}`}>
              {result.health_label}
            </span>
          )}
        </div>
        <button
          onClick={runScan}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors text-sm font-medium disabled:opacity-50"
          data-testid="ai-risk-scan-button"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Scanning...</>
          ) : result ? (
            expanded ? <><ChevronUp className="h-4 w-4" /> Hide</> : <><ChevronDown className="h-4 w-4" /> Show Results</>
          ) : (
            <><ShieldAlert className="h-4 w-4" /> Scan Roadmap</>
          )}
        </button>
      </div>

      {error && (
        <div className="px-5 py-3 text-sm text-destructive bg-destructive/5">{error}</div>
      )}

      {result && expanded && (
        <div className="p-5 space-y-5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="ai-risk-results">
          {/* Health Score */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium">Roadmap Health</span>
                <span className={`text-sm font-bold tabular-nums ${healthStyle.text}`}>{result.health_score}/10</span>
              </div>
              <div className="bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-700 ${healthStyle.bar}`}
                  style={{ width: `${result.health_score * 10}%` }}
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <p className="text-sm text-muted-foreground leading-relaxed">{result.summary}</p>

          {/* High Priority Risks */}
          {highRisks.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <span className="text-sm font-semibold text-red-400">High Priority Risks</span>
              </div>
              <div className="space-y-2">
                {highRisks.map((risk, i) => (
                  <RiskCard key={i} risk={risk} />
                ))}
              </div>
            </div>
          )}

          {/* Other Risks */}
          {otherRisks.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Other Risks</span>
              </div>
              <div className="space-y-2">
                {otherRisks.map((risk, i) => (
                  <RiskCard key={i} risk={risk} />
                ))}
              </div>
            </div>
          )}

          {/* Quick Wins */}
          {result.quick_wins?.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-primary">Quick Wins</span>
              </div>
              <ul className="space-y-2">
                {result.quick_wins.map((win, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                    <span className="text-muted-foreground">{win}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={() => { setResult(null); setExpanded(false); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="ai-risk-rescan"
          >
            Re-scan
          </button>
        </div>
      )}

      {!result && !loading && (
        <div className="px-5 py-4 text-sm text-muted-foreground">
          Analyze your roadmap for timeline conflicts, capacity overload, strategic gaps, and more.
        </div>
      )}
    </div>
  );
}

function RiskCard({ risk }) {
  const style = SEVERITY_STYLES[risk.severity] || SEVERITY_STYLES.Low;
  return (
    <div className="p-3 rounded-lg border border-border bg-muted/20" data-testid="risk-card">
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-sm font-medium">{risk.title}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium shrink-0 ${style}`}>
          {risk.severity}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
      {risk.affected_items?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {risk.affected_items.map((item, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-secondary rounded-md text-muted-foreground">
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
